import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  Badge,
  Box,
  Divider,
  Group,
  Paper,
  Pagination,
  Skeleton,
  Select,
  Stack,
  Text,
  TextInput,
  Title,
  useMantineColorScheme,
} from "@mantine/core";
import ApiSession from "../../api/index.js";
import { getErrorMessage } from "../../utils/errorUtils.js";
import formatToUTCWithGMT from "../../utils/dateHelper";
import displayError from "../../constants/utils.js";

const apiSession = new ApiSession();

const TicketTable = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { colorScheme } = useMantineColorScheme();
  const isLightMode = colorScheme === "light";

  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState("10");
  const [sortOrder, setSortOrder] = useState("desc");

  const statusBadge = (status) => {
    const s = String(status || "").toLowerCase();
    if (s === "open" || s === "submitted") return { color: "green", label: "Open" };
    if (s === "closed" || s === "resolved") return { color: "gray", label: "Closed" };
    if (s === "inprogress" || s === "in_progress") return { color: "blue", label: "In progress" };
    return { color: "blue", label: status || "Status" };
  };

  const handleTicketIdClick = async (ticketId) => {
    if (!ticketId) return;
    try {
      const response = await apiSession.getTicketId(ticketId);
      const data = response?.data ?? response;
      navigate(`/tickets/${ticketId}`, { state: { ticket: data } });
    } catch (e) {
      const message = getErrorMessage(e, t("tickets.loadTicketFailed"));
      // eslint-disable-next-line no-console
      console.error(message, e);
      displayError("error", message);
      navigate(`/tickets/${ticketId}`);
    }
  };

  const filteredTickets = useMemo(() => {
    const q = String(query || "").trim().toLowerCase();
    const base = q
      ? tickets.filter((tk) => {
          const hay = [
            tk?.ticketId,
            tk?.ticketType,
            tk?.ticketDesc,
            tk?.createdBy,
            tk?.status,
            tk?.createdOn,
          ]
            .filter((v) => v !== undefined && v !== null)
            .join(" ")
            .toLowerCase();
          return hay.includes(q);
        })
      : tickets;

    const getDateValue = (tk) => {
      const openedOn = tk?.createdOn || tk?.openedOn || tk?.createdDate || tk?.openedDate;
      if (!openedOn) return null;
      const date = openedOn instanceof Date ? openedOn : new Date(openedOn);
      return Number.isNaN(date.getTime()) ? null : date.getTime();
    };

    const getNumericId = (tk) => {
      const id = Number(tk?.ticketId);
      return Number.isNaN(id) ? null : id;
    };

    return [...base].sort((a, b) => {
      const aDate = getDateValue(a);
      const bDate = getDateValue(b);

      if (aDate !== null && bDate !== null && aDate !== bDate) {
        return sortOrder === "asc" ? aDate - bDate : bDate - aDate;
      }

      if (aDate !== null && bDate === null) return -1;
      if (aDate === null && bDate !== null) return 1;

      const aId = getNumericId(a);
      const bId = getNumericId(b);
      if (aId !== null && bId !== null && aId !== bId) {
        return sortOrder === "asc" ? aId - bId : bId - aId;
      }

      const aKey = String(a?.ticketId || "");
      const bKey = String(b?.ticketId || "");
      return sortOrder === "asc" ? aKey.localeCompare(bKey) : bKey.localeCompare(aKey);
    });
  }, [tickets, query, sortOrder]);

  useEffect(() => {
    setPage(1);
  }, [query, sortOrder]);

  const { paginatedTickets, totalPages } = useMemo(() => {
    const size = Math.max(1, Number(pageSize) || 10);
    const total = filteredTickets.length;
    const pages = Math.max(1, Math.ceil(total / size));
    const safePage = Math.min(Math.max(1, page), pages);
    const start = (safePage - 1) * size;
    const end = start + size;
    return {
      totalPages: pages,
      paginatedTickets: filteredTickets.slice(start, end),
    };
  }, [filteredTickets, page, pageSize]);

  const loadTickets = async () => {
    setLoading(true);
    try {
      const response = await apiSession.getAllTickets();
      // backend might return array directly or wrap in { data: [...] }
      const data = Array.isArray(response) ? response : response?.data;
      setTickets(Array.isArray(data) ? data : []);
    } catch (e) {
      const message = getErrorMessage(e, t("tickets.loadFailed"));
      // follow existing pattern: use snackbar in parent if needed; for now log
      // eslint-disable-next-line no-console
      console.error(message, e);
      setTickets([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTickets();
  }, []);

  const formatRelativeTime = (value) => {
    if (!value) return "-";
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return "-";

    const diffMs = Date.now() - date.getTime();
    if (diffMs < 0) return "just now";

    const seconds = Math.floor(diffMs / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days}d ago`;
    const months = Math.floor(days / 30);
    if (months < 12) return `${months}mo ago`;
    const years = Math.floor(months / 12);
    return `${years}y ago`;
  };

  const cardBg = isLightMode ? "#FFFFFF" : "var(--se-background-alternative)";
  const cardBorder = isLightMode ? "2px solid #adb5bd" : "2px solid #484343";
  const subtleText = isLightMode ? "dimmed" : "gray.4";

  return (
    <se-container>
            <se-snackbar
        id="snackbar2"
        icon="information_stroke"
        canClose={true}
      ></se-snackbar>
    {/* Page/list width: increase `maxWidth` (or set to "100%") to make cards wider */}
    <Box sx={{ padding: 16, maxWidth: 1200, width: "100%", margin: "0 auto" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 16,
          marginBottom: 12,
          flexWrap: "wrap",
        }}
      >
        <Title order={4} sx={{ fontFamily: "Nunito", margin: 0 }}>
          {t("tickets.title")}
        </Title>
        <TextInput
          value={query}
          onChange={(e) => setQuery(e.currentTarget.value)}
          placeholder={t("tickets.searchPlaceholder")}
          sx={{ width: 320, maxWidth: "100%" }}
        />
        <Select
          value={sortOrder}
          onChange={(value) => setSortOrder(value || "desc")}
          data={[
            { value: "desc", label: t("tickets.sortNewest") },
            { value: "asc", label: t("tickets.sortOldest") },
          ]}
          withinPortal
          sx={{ width: 180 }}
          aria-label={t("tickets.sortOrder")}
        />
      </Box>

      <Stack spacing={10}>
        {loading &&
          Array.from({ length: 4 }).map((_, idx) => (
            <Paper
              key={`skeleton-${idx}`}
              radius="md"
              p={25}
              sx={{
                width: "100%",
                backgroundColor: cardBg,
                border: cardBorder,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "baseline", gap: 10, minWidth: 0, marginBottom: 8 }}>
                <Skeleton height={12} width={60} />
                <Skeleton height={16} width="40%" />
              </Box>
              <Divider sx={{ marginTop: 6, marginBottom: 6 }} />
              <Group spacing={16} sx={{ flexWrap: "wrap" }}>
                <Skeleton height={16} width={140} />
                <Skeleton height={16} width={140} />
                <Skeleton height={16} width={140} />
                <Skeleton height={16} width={180} />
              </Group>
            </Paper>
          ))}

        {!loading && paginatedTickets.map((tk) => {
          const b = statusBadge(tk?.status);
          const openedOn = tk?.createdOn || tk?.openedOn || tk?.createdDate || tk?.openedDate;
          const updatedOn = tk?.updatedOn || tk?.modifiedDate;
          return (
            <Paper
              key={String(tk?.ticketId)}
              radius="md"
              p={25}
              sx={{
                width: "100%", // take full available row width
                backgroundColor: cardBg,
                border: cardBorder,
                cursor: "pointer",
                transition: "box-shadow 150ms ease, transform 150ms ease",
                "&:hover": {
                  boxShadow: isLightMode ? "0 6px 18px rgba(0,0,0,0.08)" : "0 6px 18px rgba(0,0,0,0.35)",
                  transform: "translateY(-1px)",
                },
              }}
              onClick={(event) => {
                event?.stopPropagation();
                handleTicketIdClick(tk?.ticketId);
              }}
            >
              <Box sx={{ display: "flex", alignItems: "baseline", gap: 10, minWidth: 0 }}>
                <Text
                  size="sm"
                  sx={{
                    fontFamily: "Nunito",
                    whiteSpace: "nowrap",
                  }}
                  title={String(tk?.ticketId ?? "")}
                >
                  <Text span size="sm" weight={600} sx={{ fontFamily: "Nunito" }}>
                    #{tk?.ticketId ?? "-"}
                  </Text>
                </Text>

                <Text
                  size="md"
                  sx={{
                    fontFamily: "Nunito",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    flex: 1,
                    minWidth: 0,
                  }}
                  title={tk?.ticketTitle || ""}
                >
                  {tk?.ticketTitle || "-"}
                </Text>
              </Box>

              <Divider sx={{ marginTop: 6, marginBottom: 6 }} />

              <Group
                spacing={0}
                sx={{
                  flexWrap: "wrap",
                  justifyContent: "space-between",
                  rowGap: 8,
                  columnGap: 16,
                  "& > *": {
                    flex: "1 1 0",
                  },
                  "@media (max-width: 900px)": {
                    justifyContent: "flex-start",
                    "& > *": {
                      flex: "0 0 auto",
                    },
                  },
                }}
              >
                <Text size="sm" sx={{ fontFamily: "Nunito", whiteSpace: "nowrap" }}>
                  <Text span size="sm" color={subtleText} sx={{ fontFamily: "Nunito" }}>
                    {t("tickets.columns.status")} :
                  </Text>{" "}
                  <Badge color={b.color} variant="filled" radius="sm" sx={{ verticalAlign: "middle" }}>
                    {b.label}
                  </Badge>
                </Text>

                <Text size="sm" sx={{ fontFamily: "Nunito", whiteSpace: "nowrap" }}>
                  <Text span size="sm" color={subtleText} sx={{ fontFamily: "Nunito" }}>
                    {t("tickets.columns.type")} :
                  </Text>{" "}
                  <Text span size="sm" sx={{ fontFamily: "Nunito" }}>
                    {tk?.ticketType || "-"}
                  </Text>
                </Text>

                <Text size="sm" sx={{ fontFamily: "Nunito", whiteSpace: "nowrap" }}>
                  <Text span size="sm" color={subtleText} sx={{ fontFamily: "Nunito" }}>
                    {t("tickets.openedOn")} :
                  </Text>{" "}
                  <Text span size="sm" sx={{ fontFamily: "Nunito" }}>
                    {formatRelativeTime(formatToUTCWithGMT(updatedOn))}
                  </Text>
                </Text>

                <Text
                  size="sm"
                  sx={{
                    fontFamily: "Nunito",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: 360,
                  }}
                  title={String(tk?.createdBy || tk?.openedBy || "")}
                >
                  <Text span size="sm" color={subtleText} sx={{ fontFamily: "Nunito" }}>
                    {t("tickets.openedBy")} :
                  </Text>{" "}
                  <Text span size="sm" weight={600} sx={{ fontFamily: "Nunito" }}>
                    {tk?.createdBy || tk?.openedBy || "-"}
                  </Text>
                </Text>
              </Group>
            </Paper>
          );
        })}
      </Stack>

      {!loading && filteredTickets.length === 0 && (
        <Box sx={{ padding: 16 }}>
          <Text sx={{ fontFamily: "Nunito" }} color={subtleText}>
            {t("tickets.empty")}
          </Text>
        </Box>
      )}

      {!loading && filteredTickets.length > 0 && (
        <Group position="apart" align="center" sx={{ marginTop: 16,paddingBottom:16, paddingTop: 8 }}>
          <Group spacing="xs" align="center">
            <Text size="sm" color={subtleText} sx={{ fontFamily: "Nunito" }}>
              {t("common.rowsPerPage")}
            </Text>
            <Select
              value={pageSize}
              onChange={(v) => {
                setPageSize(v || "10");
                setPage(1);
              }}
              data={["5", "10", "20", "50"]}
              withinPortal
              sx={{ width: 100 }}
            />
            <Text size="sm" color={subtleText} sx={{ fontFamily: "Nunito" }}>
              {t("tickets.paginationCount", { count: filteredTickets.length })}
            </Text>
          </Group>

          <Pagination value={page} onChange={setPage} total={totalPages} />
        </Group>
      )}
    </Box>
    </se-container>
  );
};

export default TicketTable;
